import {
  FORMAT_LABELS,
  PHYSICAL_VENUE_MODELS,
  LINEAR_FORMATS,
  formatAgeDisplay,
  labelsFromSlugs,
  PLATFORM_LABELS,
  DEVICE_LABELS
} from './experienceOntology';

function inferPrimaryFormat(exp) {
  if (exp.primaryFormat) return exp.primaryFormat;
  if (exp.isLBE) {
    if (exp.contentType === 'live-events') return 'immersive-theater';
    return 'installation';
  }
  const category = exp.category || '';
  if (category === 'virtual-reality') return 'vr';
  if (category === 'augmented-reality') return 'ar';
  if (category === '360-video') return '360-video';
  if (category === 'metaverse') return 'social-vr';
  if (category === 'location-based') return 'lbe';
  if (category === 'for-you') return '360-video';
  return null;
}

function inferVenueModel(exp) {
  if (exp.venueModel) return exp.venueModel;
  if (exp.venueName || exp.venueAddress) return 'specific-venue';
  if (exp.isLBE || exp.category === 'location-based') return 'specific-venue';
  if (exp.medium === 'lbe') return 'specific-venue';
  if (exp.medium === 'metaverse') return 'virtual-world';
  if (exp.locationCountry || exp.venueLat != null) return 'specific-venue';
  return 'anywhere';
}

function inferAvailabilityType(exp) {
  if (exp.availabilityType) return exp.availabilityType;
  if (exp.isLBE) return 'permanent';
  if (exp.sessionStructure === 'live-event') return 'scheduled';
  if (exp.sessionStructure === 'persistent') return 'persistent';
  return null;
}

function mapLegacyDevices(supportedDevices) {
  if (!Array.isArray(supportedDevices)) return [];
  const map = {
    browser: 'browser',
    quest: 'quest',
    'vision-pro': 'vision-pro',
    pcvr: 'pcvr',
    phone: 'phone',
    psvr: 'psvr'
  };
  return supportedDevices
    .filter((d) => d !== 'lbe' && d !== 'phone-ar')
    .map((d) => map[d] || d)
    .filter(Boolean);
}

function mapLegacyPlatforms(supportedDevices, launchInBrowser) {
  const platforms = [];
  if (launchInBrowser || supportedDevices?.includes('browser')) platforms.push('browser');
  if (supportedDevices?.includes('quest')) platforms.push('quest');
  if (supportedDevices?.includes('vision-pro')) platforms.push('vision-pro');
  if (supportedDevices?.includes('pcvr')) platforms.push('pcvr');
  return platforms;
}

/** Infer how content is delivered when not explicitly set on the experience record. */
function inferDeliveryTechnology(exp, { isLegacyPhysical }) {
  if (Array.isArray(exp.deliveryTechnology) && exp.deliveryTechnology.length) {
    return exp.deliveryTechnology;
  }
  if (isLegacyPhysical) return [];
  if (exp.hostPlatform) return [];
  if (exp.isPlatformApp) return ['native-app'];

  const url = String(exp.url || exp.launchUrl || '');
  const tagStr = (exp.tags || []).join(' ').toLowerCase();

  if (/webxr/.test(tagStr) || exp.id === 'escape-artist') return ['webxr'];
  if (/webar/.test(tagStr) || url.includes('webar')) return ['webAR'];

  if (exp.launchInBrowser) return [];

  if (/apps\.apple\.com|play\.google\.com|meta\.com\/experiences/.test(url)) {
    return ['native-app'];
  }
  if (!exp.launchInBrowser && exp.supportedDevices?.some((d) => d !== 'lbe' && d !== 'browser')) {
    return ['native-app'];
  }

  return [];
}

/** Infer where users access the experience when not explicitly set. */
function inferAccessSurface(exp, devices, { isLegacyPhysical }) {
  if (Array.isArray(exp.accessSurface) && exp.accessSurface.length) {
    return exp.accessSurface;
  }
  if (isLegacyPhysical) return [];

  const url = String(exp.url || '');
  const surfaces = [];

  if (/apps\.apple\.com|play\.google\.com/.test(url) || devices.includes('phone-ar')) {
    surfaces.push('mobile-app');
  }

  if (exp.launchInBrowser || devices.includes('browser')) {
    surfaces.push('mobile-browser', 'desktop-browser');
    if (devices.some((d) => ['quest', 'vision-pro', 'pcvr'].includes(d))) {
      surfaces.push('headset-browser');
    }
  }

  if (!exp.launchInBrowser) {
    if (devices.some((d) => ['quest', 'vision-pro', 'psvr'].includes(d))) {
      surfaces.push('headset-app');
    }
    if (devices.includes('pcvr')) {
      surfaces.push('desktop-app');
    }
  }

  return [...new Set(surfaces)];
}

/**
 * Returns a view model merging legacy static/Firestore fields with new optional schema.
 * All fields remain optional; safe to use for rendering.
 */
export function normalizeExperience(exp) {
  if (!exp) return null;

  const primaryFormat = inferPrimaryFormat(exp);
  const venueModel = inferVenueModel(exp);
  const devices = exp.devices?.length
    ? exp.devices
    : mapLegacyDevices(exp.supportedDevices);
  const platforms = exp.platforms?.length
    ? exp.platforms
    : mapLegacyPlatforms(exp.supportedDevices, exp.launchInBrowser);

  const heroImageUrl = exp.heroImageUrl || exp.thumbnail || '';
  const officialDescription = exp.officialDescription || exp.description || '';
  const isLegacyPhysical =
    exp.isLBE || exp.category === 'location-based' || exp.medium === 'lbe';
  const formatBadge =
    exp.formatBadge || exp.categoryChip || (primaryFormat && FORMAT_LABELS[primaryFormat]) || '';
  const formatDescriptor =
    exp.formatDescriptor ||
    (isLegacyPhysical ? '' : exp.duration || '');

  const venueLat = exp.venueLat ?? exp.locationLat ?? null;
  const venueLng = exp.venueLng ?? exp.locationLng ?? null;
  const venueCity = exp.venueCity || exp.locationCity || '';
  const venueCountry = exp.venueCountry || exp.locationCountry || '';

  const priceType = exp.priceType || (exp.priceLabel?.toLowerCase().includes('free') ? 'free' : null);

  const comfortRating = exp.comfortRating || null;
  const ageRating = exp.ageRating || null;

  const runtimeMinutes = exp.runtimeMinutes ?? null;
  const typicalSessionLabel = exp.typicalSessionLabel || (exp.duration && !venueCity ? exp.duration : '') || '';

  const bestFor = Array.isArray(exp.bestFor) ? exp.bestFor : [];

  const galleryUrls = Array.isArray(exp.galleryUrls) ? exp.galleryUrls : [];

  const creatorName = exp.creatorName || exp.createdByEmail?.split('@')[0] || null;
  const creatorId = exp.creatorId || exp.createdBy || null;

  const deliveryTechnology = inferDeliveryTechnology(exp, { isLegacyPhysical });
  const accessSurface = inferAccessSurface(exp, devices, { isLegacyPhysical });

  return {
    ...exp,
    heroImageUrl,
    officialDescription,
    realityCheckPreview: exp.realityCheckPreview || null,
    formatBadge,
    formatDescriptor,
    primaryFormat,
    venueModel,
    platforms,
    devices,
    priceType,
    priceLabel: exp.priceLabel || null,
    publishPlatform: exp.publishPlatform || null,
    alsoAvailableOn: Array.isArray(exp.alsoAvailableOn) ? exp.alsoAvailableOn : [],
    comfortRating,
    ageRating,
    languages: Array.isArray(exp.languages) ? exp.languages : [],
    sessionStructure: exp.sessionStructure || null,
    typicalSessionLabel,
    runtimeMinutes,
    recommendedVisitTime: exp.recommendedVisitTime || null,
    groupSize: Array.isArray(exp.groupSize) ? exp.groupSize : [],
    themes: Array.isArray(exp.themes) ? exp.themes : [],
    activities: Array.isArray(exp.activities) ? exp.activities : [],
    replayability: exp.replayability || null,
    guidanceLevel: exp.guidanceLevel || null,
    multiplayer: exp.multiplayer ?? null,
    bestFor,
    effortSignal: exp.effortSignal || null,
    deliveryTechnology,
    accessSurface,
    isPlatformApp: exp.isPlatformApp === true,
    hostPlatform: exp.hostPlatform || null,
    availabilityType: inferAvailabilityType(exp),
    availabilityStatus: exp.availabilityStatus || null,
    availabilityStatusLabel: exp.availabilityStatusLabel || null,
    venueName: exp.venueName || null,
    venueAddress: exp.venueAddress || null,
    venueCity,
    venueCountry,
    venueLat,
    venueLng,
    venueHours: exp.venueHours || null,
    venueParking: exp.venueParking || null,
    venueTransit: exp.venueTransit || null,
    venueAccessibilityEntrance: exp.venueAccessibilityEntrance || null,
    comePrepared: Array.isArray(exp.comePrepared) ? exp.comePrepared : [],
    locomotionTypes: Array.isArray(exp.locomotionTypes) ? exp.locomotionTypes : [],
    contentNotes: Array.isArray(exp.contentNotes) ? exp.contentNotes : [],
    accessibilityFeatures: Array.isArray(exp.accessibilityFeatures) ? exp.accessibilityFeatures : [],
    physicalRequirements: Array.isArray(exp.physicalRequirements) ? exp.physicalRequirements : [],
    verifiedBy: exp.verifiedBy || null,
    lastUpdated: exp.lastUpdated || null,
    editorialBadge: exp.editorialBadge || null,
    editorialBadgeDetail: exp.editorialBadgeDetail || null,
    partOfCollection: exp.partOfCollection || null,
    galleryUrls,
    trailerUrl: exp.trailerUrl || null,
    creatorName,
    creatorId,
    creatorVerified: exp.creatorVerified ?? false,
    creatorExperienceCount: exp.creatorExperienceCount ?? null,
    creatorBio: exp.creatorBio || null,
    creatorLogoUrl: exp.creatorLogoUrl || null,
    tourStops: Array.isArray(exp.tourStops) ? exp.tourStops : [],
    launchUrl: exp.url || exp.launchUrl || '',
    ageDisplay: formatAgeDisplay(ageRating),
    platformLabels: labelsFromSlugs(platforms, PLATFORM_LABELS),
    deviceLabels: labelsFromSlugs(devices, DEVICE_LABELS),
    isPhysicalVenue: PHYSICAL_VENUE_MODELS.includes(venueModel),
    isLinearFormat: LINEAR_FORMATS.includes(primaryFormat)
  };
}

export function getExperienceShareUrl(experienceId) {
  if (typeof window === 'undefined') return `#/exp/${experienceId}`;
  return `${window.location.origin}${window.location.pathname}#/exp/${experienceId}`;
}

export function getExperienceCanonicalUrl(exp) {
  if (exp?.launchUrl) return exp.launchUrl;
  if (exp?.url) return exp.url;
  return getExperienceShareUrl(exp?.id);
}

export function getRealityCheckPreviewText(rawExp) {
  const normalized = normalizeExperience(rawExp);
  if (!normalized) return '';
  if (normalized.realityCheckPreview?.trim()) {
    return normalized.realityCheckPreview.trim();
  }
  return normalized.officialDescription?.trim() || '';
}

/** @deprecated use getRealityCheckPreviewText */
export function getPreviewText(rawExp) {
  return getRealityCheckPreviewText(rawExp);
}

export function shouldShowAboutSection(rawExp) {
  const normalized = normalizeExperience(rawExp);
  const official = normalized?.officialDescription?.trim();
  if (!official) return false;

  const previewField = normalized.realityCheckPreview?.trim();
  if (!previewField) return false;

  return official !== previewField;
}
